import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const alertVariants = cva(
  "relative grid w-full grid-cols-[0_1fr] items-start gap-y-0.5 rounded-lg border px-4 py-3 text-sm has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr] has-[>svg]:gap-x-3 [&>svg]:size-4 [&>svg]:translate-y-0.5 [&>svg]:text-current",
  {
    variants: {
      variant: {
        default: "bg-card text-card-foreground",
        destructive:
          "bg-card text-destructive *:data-[slot=alert-description]:text-destructive/90 [&>svg]:text-current",
        warning:
          "border-amber-500/40 bg-amber-50 text-amber-900 *:data-[slot=alert-description]:text-amber-900/80 [&>svg]:text-amber-600 dark:border-amber-400/40 dark:bg-amber-900/20 dark:text-amber-100 dark:*:data-[slot=alert-description]:text-amber-100/80 dark:[&>svg]:text-amber-300",
        success:
          "border-emerald-500/40 bg-emerald-50 text-emerald-900 *:data-[slot=alert-description]:text-emerald-900/80 [&>svg]:text-emerald-600 dark:border-emerald-400/40 dark:bg-emerald-900/20 dark:text-emerald-100 dark:*:data-[slot=alert-description]:text-emerald-100/80 dark:[&>svg]:text-emerald-300",
        info: "border-sky-500/40 bg-sky-50 text-sky-900 *:data-[slot=alert-description]:text-sky-900/80 [&>svg]:text-sky-600 dark:border-sky-400/40 dark:bg-sky-900/20 dark:text-sky-100 dark:*:data-[slot=alert-description]:text-sky-100/80 dark:[&>svg]:text-sky-300",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

function Alert({
  className,
  variant,
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof alertVariants>) {
  return (
    <div
      data-slot="alert"
      role="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    />
  )
}

function AlertTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-title"
      className={cn(
        "col-start-2 line-clamp-1 min-h-4 font-medium tracking-tight",
        className
      )}
      {...props}
    />
  )
}

function AlertDescription({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        "col-start-2 grid justify-items-start gap-1 text-sm text-muted-foreground [&_p]:leading-relaxed",
        className
      )}
      {...props}
    />
  )
}

export { Alert, AlertTitle, AlertDescription }
